import React, { useRef, useState } from 'react';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';

export default function PaymentForm({ clientSecret, totalCents, setError, onSuccess, onPending }: {
    clientSecret: string; totalCents?: number; setError: (message: string) => void;
    onSuccess: (intent: any) => Promise<void> | void; onPending?: () => Promise<void> | void;
}) {
    const stripe = useStripe();
    const elements = useElements();
    const inFlight = useRef(false);
    const [busy, setBusy] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const submit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!stripe || !elements || inFlight.current) return;
        inFlight.current = true; setBusy(true); setError('');
        let submittedToStripe = false;
        try {
            const validation = await elements.submit();
            if (validation.error) { setError(validation.error.message || 'Check your payment details'); return; }
            submittedToStripe = true;
            const result = await stripe.confirmPayment({ elements, clientSecret,
                confirmParams: { return_url: window.location.href.split('?')[0] }, redirect: 'if_required' });
            if (result.error) {
                if (['card_error', 'validation_error'].includes(result.error.type)) {
                    submittedToStripe = false; setError(result.error.message || 'Check your payment details');
                } else {
                    setConfirming(true); setError('Confirming your payment. Please do not pay again.');
                    await onPending?.();
                }
                return;
            }
            setConfirming(true);
            if (result.paymentIntent?.status === 'succeeded') await onSuccess(result.paymentIntent);
            else await onPending?.();
        } catch {
            // An uncertain result must be reconciled, never presented as permission to pay again.
            setError(submittedToStripe ? 'Confirming your payment. Please keep this page open; do not pay again.' : 'Could not validate payment details. Please retry.');
            setConfirming(submittedToStripe);
        } finally {
            if (!submittedToStripe) { inFlight.current = false; setBusy(false); }
        }
    };
    return <form aria-label="Payment" onSubmit={submit}>
        <PaymentElement />
        <button type="submit" disabled={busy || !stripe || !elements}
            className="Payment-submit w-full min-h-[56px] rounded-[4px] px-6 py-4 mt-6 text-lg font-bold text-center transition-opacity enabled:hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed">
            {confirming ? 'Confirming payment…' : busy ? 'Processing…' : totalCents === undefined ? 'Pay' : `Pay $${(totalCents / 100).toFixed(2)}`}
        </button>
    </form>;
}
